import React, { createContext, useContext, useEffect, useRef } from "react";

import { SERVICE_STATUS_IMPRESSION } from "../../constants/Events";
import { useTrackEvent } from "../../libs/analytics";
import {
  UNKNOWN_STATUS_VIEW,
  type ServiceStatusView,
} from "./service-status.model";
import { useServiceStatusFeed } from "./use-service-status-feed";

// Consumers rendered outside the provider (and unmocked tests) get the silent
// unknown view rather than a crash or a false "operational".
const ServiceStatusContext =
  createContext<ServiceStatusView>(UNKNOWN_STATUS_VIEW);

export function ServiceStatusProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const view = useServiceStatusFeed();
  const trackEvent = useTrackEvent();

  // One impression per distinct thing shown, per session — covers the banner
  // states and the badge-only maintenance state. A dismissed banner still
  // counts, because the header dot is still telling the user something.
  const seenRef = useRef(new Set<string>());
  const isNotable =
    !view.isError && view.dot !== "green" && view.dot !== "none";

  useEffect(() => {
    if (!isNotable || seenRef.current.has(view.bannerKey)) return;
    seenRef.current.add(view.bannerKey);
    trackEvent({
      eventName: SERVICE_STATUS_IMPRESSION,
      metadata: {
        state: view.overall,
        banner: view.bannerState,
        incidentTitle: view.incident?.title ?? view.maintenance?.title,
      },
      useServices: ["AMP", "HS"],
    });
  }, [
    isNotable,
    trackEvent,
    view.bannerKey,
    view.bannerState,
    view.incident?.title,
    view.maintenance?.title,
    view.overall,
  ]);

  return (
    <ServiceStatusContext.Provider value={view}>
      {children}
    </ServiceStatusContext.Provider>
  );
}

export function useServiceStatus(): ServiceStatusView {
  return useContext(ServiceStatusContext);
}
